import { DocumentServicesObject } from '@wix/document-services-types';

export const HostTypes = {
  Classic: 'classic' as const,
  Editorx: 'editorx' as const,
  Editor3: 'editor3' as const,
};

export const ConsumerTypes = {
  PanelBuilder: 'panel-builder' as const,
  Internal: 'internal' as const,
};

export const ApiVersions = {
  V1: 'v1' as const,
  V2: 'v2' as const,
};

export type IContextBasedFactory<T> = (context: IContext) => T;

export type IHostType = (typeof HostTypes)[keyof typeof HostTypes];
export type IConsumerType = (typeof ConsumerTypes)[keyof typeof ConsumerTypes];
export type IAPIVersions = (typeof ApiVersions)[keyof typeof ApiVersions];

export type IContext = {
  appDefinitionId: string;
  applicationId: number;
};

type AnyFunction = (...args: any[]) => any;

export type IDocumentServices = {
  runInContext: <F extends AnyFunction>(
    context: string,
    fn: F,
  ) => ReturnType<F>;
} & DocumentServicesObject;

export type IFindByHostType<
  TWhere extends IPlatformAPIDefinition,
  T extends IHostType,
> = TWhere extends {
  hostType: infer InferredT;
}
  ? InferredT extends T
    ? TWhere
    : never
  : never;

// read more: https://stackoverflow.com/a/50375286
export type IUnionToIntersection<U> = (
  U extends any ? (k: U) => void : never
) extends (k: infer I) => void
  ? I
  : never;

type IPlatformAPIDefinition = {
  consumerType: IConsumerType;
  hostType: IHostType;
  version: IAPIVersions;
  platformAPI: unknown;
  superAPI: unknown;
};

type ICreateNamespaceParams<T, K, P, F extends IPlatformAPIDefinition> = {
  consumerType: T;
  hostType: K;
  version: P;
  superAPI: F['superAPI'];
  context: IContext;
};

type INamespaceCreatorCallback<F extends IPlatformAPIDefinition> = <
  T extends IConsumerType,
  K extends IHostType,
  P extends IAPIVersions,
>(
  config: ICreateNamespaceParams<T, K, P, F>,
) => F['platformAPI'] | null;

type INamespaceCreatorReturnType<F extends IPlatformAPIDefinition> = <
  T extends IConsumerType,
  K extends IHostType,
  P extends IAPIVersions,
>(
  config: ICreateNamespaceParams<T, K, P, F>,
) => Extract<F, { consumerType: T; version: P }>['platformAPI'];

export function definePlatformAPI<T extends IPlatformAPIDefinition>(
  creator: (superAPI: T['superAPI']) => IContextBasedFactory<T['platformAPI']>,
) {
  return creator;
}

// NOTE:
// INamespaceCreatorCallback is used to hint return types inside callback
// INamespaceCreatorReturnType is used to type hint the return type and it infers correct type from generic
// but in fact those two are the same
export function defineNamespace<F extends IPlatformAPIDefinition>(
  namespaceCreator: INamespaceCreatorCallback<F>,
): INamespaceCreatorReturnType<F> {
  return namespaceCreator;
}
